import {ICashierPaymentsApi, PaymentMethod} from '@wix/cashier-payments-widget';
import {getCountryFromLocaleDataset} from './localeDataset.util';
import {type MFieldName, MFieldValuePairs} from '@wix/cashier-payments-widget/src/types/MandatoryFields';
import {isCountryWithVat} from './isCountryWithVat';
import {CashierMandatoryField, CashierMandatoryFieldsOverrides} from '../../types/payment.types';
import {CartModel} from '../models/checkout/Cart/Cart.model';
import {HOLDER_ID_FIELD, MANDATORY_FIELDS_HIDDEN} from '../../common/constants';

const MANDATORY_FIELDS_PLACEHOLDER = '-';

export const mapStateFromISO_3166To2Letters = (state: string): string => {
  const reg = /.*-/g;
  return state.replace(reg, '');
};

export function getMandatoryFieldsByCheckout(cart: CartModel, contactCountry?: string): MFieldValuePairs {
  const country = contactCountry ?? cart.paymentInfo.address?.country ?? cart.deliveryInfo.address?.country;
  const countryCode = country ? getCountryFromLocaleDataset(country)?.key : MANDATORY_FIELDS_PLACEHOLDER;
  const state = cart.paymentInfo.address?.subdivision
    ? mapStateFromISO_3166To2Letters(cart.paymentInfo.address?.subdivision)
    : MANDATORY_FIELDS_PLACEHOLDER;
  return {
    countryCode,
    state,
    holderId:
      cart.paymentInfo.contact?.vatId?.id ?? (isCountryWithVat(country) ? MANDATORY_FIELDS_PLACEHOLDER : undefined),
    email: cart.customerInfo.email ?? MANDATORY_FIELDS_PLACEHOLDER,
    firstName: cart.paymentInfo.contact?.firstName ?? MANDATORY_FIELDS_PLACEHOLDER,
    lastName: cart.paymentInfo.contact?.lastName ?? MANDATORY_FIELDS_PLACEHOLDER,
    street: cart.paymentInfo.address?.streetAddress?.name ?? MANDATORY_FIELDS_PLACEHOLDER,
    houseNumber: cart.paymentInfo.address?.streetAddress?.number ?? MANDATORY_FIELDS_PLACEHOLDER,
    address:
      cart.paymentInfo.address?.addressLine ??
      (cart.paymentInfo.address?.streetAddress?.name
        ? `${cart.paymentInfo.address.streetAddress.name} ${cart.paymentInfo.address.streetAddress.number!}`
        : MANDATORY_FIELDS_PLACEHOLDER),
    city: cart.paymentInfo.address?.city ?? MANDATORY_FIELDS_PLACEHOLDER,
    zipCode: cart.paymentInfo.address?.postalCode || MANDATORY_FIELDS_PLACEHOLDER,
    phone: cart.paymentInfo.contact?.phone ?? MANDATORY_FIELDS_PLACEHOLDER,
  };
}

export function getHiddenFieldsByCheckout(cart: CartModel, contactCountry?: string): string[] {
  const country = contactCountry ?? cart.paymentInfo.address?.country ?? cart.deliveryInfo.address?.country;
  const shouldHideVatId = cart.paymentInfo.contact?.vatId?.id ?? isCountryWithVat(country);

  if (shouldHideVatId) {
    return [...MANDATORY_FIELDS_HIDDEN, HOLDER_ID_FIELD];
  }

  return MANDATORY_FIELDS_HIDDEN;
}

export const cashierWidgetEwalletPaymentMethod: string[] = [PaymentMethod.PayPal, PaymentMethod.GooglePay];

export const getMandatoryFieldsOverrides = async (
  paymentsApi: ICashierPaymentsApi | undefined,
  country?: string | null
): Promise<CashierMandatoryFieldsOverrides> => {
  //@ts-expect-error cashier api
  const mandatoryFields: MFieldName[] = (await paymentsApi?.fetchMandatoryFields?.(country)) ?? [];
  return getCashierMandatoryFieldsOverrides(mandatoryFields);
};

export const updateMandatoryFields = async (
  paymentsApi: ICashierPaymentsApi | undefined,
  setCashierMandatoryFields: (cashierMandatoryFields: CashierMandatoryFieldsOverrides) => void,
  country?: string | null
): Promise<void> => {
  const cashierMandatoryFieldsOverrides = await getMandatoryFieldsOverrides(paymentsApi, country);
  setCashierMandatoryFields(cashierMandatoryFieldsOverrides);
};

export function getCashierMandatoryFieldsOverrides(mandatoryFields: MFieldName[]) {
  const cashierMandatoryFieldsOverrides: CashierMandatoryFieldsOverrides = {};

  const formattedCashierMandatoryFields =
    mandatoryFields
      ?.map((field) => {
        return CashierMandatoryField[field as CashierMandatoryField];
      })
      .filter((field) => Boolean(field)) ?? /* istanbul ignore next */ [];

  formattedCashierMandatoryFields.forEach((field) => {
    cashierMandatoryFieldsOverrides[field] = true;
  });

  return cashierMandatoryFieldsOverrides;
}

export const isPayPalSelected = (activePaymentId?: string) => activePaymentId === PaymentMethod.PayPal;
export const isManualPaymentSelected = (activePaymentId?: string) => activePaymentId === PaymentMethod.Offline;
